'use client';

/**
 * 全站只挂一处：PayPal 整页跳转回跳后，统一从 URL 上的 option/order_id/subscription_id 等
 * 参数完成 commerce confirm。与既有 FirebaseLoginRedirectHandler / FacebookOAuthHashHandler 对齐。
 *
 * 设计要点：
 * 1. Safari 剥参时先用 session 中的 return_url 同源 replace 恢复，再解析 confirm；
 * 2. 解析优先使用 window.location.search，避免 useSearchParams 首帧为空；
 * 3. 模块级 flag 防 React 18 StrictMode 双调；
 * 4. confirm 后 replaceState 清 PayPal query，避免 F5 重提；
 * 5. sessionStorage 标记防多 tab/双消费。
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';

import dynamic from 'next/dynamic';

import { CommerceApi } from '@/apis/commerce';
import LoadingAnimation from '@/components/loading-animation';
import { assertConfirmOrderResponse, confirmCommerceOrderWithPolling } from '@/hooks/use-pay/confirm-with-polling';
import { clearPersistedPayPendingMeta, readPersistedPayPendingMeta } from '@/hooks/use-pay/paypal-checkout-redirect';
import {
    EPayPalReturnQueryKey,
    parsePayPalReturnFromSearchParams,
    stripPayPalReturnFromUrl,
} from '@/lib/pay/paypal-return-params';
import {
    clearPayPendingReturnUrls,
    isBackForwardNavigation,
    readPayPendingReturnUrls,
    tryRestoreStrippedPayReturnUrl,
} from '@/lib/pay/paypal-return-restore';
import { useToast } from '@/providers/root/toast';
import { useAssetStore } from '@/store/asset';
import {
    formatConfirmOrderFailDetail,
    mapConfirmStatusToPayPalFailReason,
    readArmsFailDetail,
    reportPayPalFail,
    reportPayPalSuccess,
    type TPayPalArmsFailReason,
} from '@/utils/arms/paypal-events';
import {
    queuePayResultTrackingAfterRefresh,
    trackRechargePageResultPayCancel,
    trackRechargePageResultPayFail,
    type IRechargePayExtras,
    type IRechargeResultStaticContext,
} from '@/utils/tracker/pay';

// 弹窗仅在取消支付时展示，懒加载以减少首屏 bundle 体积
// 弹窗仅在取消支付时展示，懒加载以减少首屏 bundle 体积；
// fallback null：加载期间不渲染占位，避免在弹窗尚未准备好时闪烁
const PayPalCancelReasonModal = dynamic(() => import('@/components/paypal-cancel-reason-modal'), {
    ssr: false,
    loading: () => null,
});

let hasProcessedReturn = false;

const CONSUMED_KEY = (orderId: string): string => `paypal_consumed_${orderId}`;

const DEFAULT_STORE_FOR_CONFIRM = 'PAYPAL';

function getReturnSearchParams(): URLSearchParams {
    if (typeof window === 'undefined') {
        return new URLSearchParams();
    }
    return new URLSearchParams(window.location.search);
}

function metaToStaticContext(
    meta: NonNullable<ReturnType<typeof readPersistedPayPendingMeta>>
): IRechargeResultStaticContext {
    return {
        trigger_scene: meta.trigger_scene,
        gear_id: meta.gear_id,
        is_balance_enough: meta.is_balance_enough,
        paywall_template_id: meta.paywall_template_id,
        version: meta.version ?? 0,
        checksum: meta.checksum ?? '',
        book_id: meta.book_id,
        chapter_id: meta.chapter_id,
        chapter_price: meta.chapter_price,
        is_auto_unlock_on: meta.is_auto_unlock_on,
    };
}

function metaToPayExtras(meta: NonNullable<ReturnType<typeof readPersistedPayPendingMeta>>): IRechargePayExtras {
    return {
        money: meta.money,
        goods_type: meta.goods_type,
        is_renew: meta.is_renew,
        coin: meta.coin,
        bonus: meta.bonus,
    };
}

function buildPayPalArmsPropsFromMeta(
    meta: NonNullable<ReturnType<typeof readPersistedPayPendingMeta>> | null,
    returnScene: string
) {
    const ctx = meta ? metaToStaticContext(meta) : null;
    const extras = meta ? metaToPayExtras(meta) : null;
    return {
        return_scene: returnScene,
        goods_type: extras?.goods_type ?? ('recharge' as const),
        gear_id: ctx?.gear_id ?? '',
        trigger_scene: ctx?.trigger_scene ?? '',
        paywall_template_id: ctx?.paywall_template_id,
        book_id: ctx?.book_id,
        chapter_id: ctx?.chapter_id,
        money: extras?.money,
    };
}

function reportPayPalSuccessFromMeta(
    orderId: string,
    meta: NonNullable<ReturnType<typeof readPersistedPayPendingMeta>> | null,
    returnScene: string
): void {
    reportPayPalSuccess({
        order_id: orderId,
        ...buildPayPalArmsPropsFromMeta(meta, returnScene),
    });
}

function reportPayPalFailFromMeta(
    orderId: string,
    meta: NonNullable<ReturnType<typeof readPersistedPayPendingMeta>> | null,
    returnScene: string,
    failReason: TPayPalArmsFailReason,
    failDetail?: string
): void {
    reportPayPalFail({
        ...buildPayPalArmsPropsFromMeta(meta, returnScene),
        order_id: orderId || undefined,
        fail_reason: failReason,
        fail_detail: failDetail,
    });
}

export default function PayPalReturnConfirmHandler() {
    const t = useTranslations('Paywall');

    const { addToast } = useToast();
    const [isConfirming, setIsConfirming] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    // 订单ID 用于手势返回场景的取消上报，回跳场景以 URL 参数为准
    const [orderId, setOrderId] = useState('');
    const handleCancelModalClose = useCallback(() => setShowCancelModal(false), []);

    useEffect(() => {
        if (hasProcessedReturn) {
            return;
        }

        if (tryRestoreStrippedPayReturnUrl()) {
            return;
        }

        const returnSearchParams = getReturnSearchParams();
        const ret = parsePayPalReturnFromSearchParams(returnSearchParams);
        if (!ret) {
            // 手势返回（back_forward 导航）：用户从 PayPal 手势返回，session 中仍有 pending 数据
            const pending = isBackForwardNavigation() ? readPayPendingReturnUrls() : null;
            if (pending) {
                hasProcessedReturn = true;
                const meta = readPersistedPayPendingMeta(pending.orderId);
                reportPayPalFailFromMeta(pending.orderId, meta, '', 'paypal_return_cancel');
                setOrderId(pending.orderId);
                // 侧滑取消：用户从 PayPal 页手势返回（back_forward 导航）
                if (meta) {
                    trackRechargePageResultPayCancel({
                        result_status: 'slip_cancel',
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                    });
                }
                addToast('Payment canceled.');
                setShowCancelModal(true);
                if (pending.orderId) {
                    clearPersistedPayPendingMeta(pending.orderId);
                }
                clearPayPendingReturnUrls();
            }
            return;
        }
        const returnScene = returnSearchParams.get(EPayPalReturnQueryKey.ReturnScene)?.trim() ?? '';
        hasProcessedReturn = true;

        try {
            const cleanUrl = stripPayPalReturnFromUrl(window.location.href);
            if (cleanUrl !== window.location.pathname + window.location.search + window.location.hash) {
                window.history.replaceState(window.history.state, '', cleanUrl);
            }
        } catch {
            // history.replaceState 在极端环境可能抛错，sessionStorage 防重仍可兜底
        }

        const meta = readPersistedPayPendingMeta(ret.orderId);
        const store = meta?.store || DEFAULT_STORE_FOR_CONFIRM;
        setOrderId(ret.orderId);

        if (ret.status === 'cancel') {
            reportPayPalFailFromMeta(ret.orderId, meta, returnScene, 'paypal_return_cancel');
            // 点击取消：PayPal 回跳 URL option === 'cancel' | 'cancelled'
            if (meta) {
                trackRechargePageResultPayCancel({
                    result_status: 'click_cancel',
                    staticContext: metaToStaticContext(meta),
                    payExtras: metaToPayExtras(meta),
                });
            }
            addToast('Payment canceled.');
            setShowCancelModal(true);
            if (ret.orderId) {
                clearPersistedPayPendingMeta(ret.orderId);
            }
            clearPayPendingReturnUrls();
            return;
        }

        let alreadyConsumed = false;
        try {
            const consumedKey = CONSUMED_KEY(ret.orderId);
            if (window.sessionStorage.getItem(consumedKey) === '1') {
                alreadyConsumed = true;
            } else {
                window.sessionStorage.setItem(consumedKey, '1');
            }
        } catch {
            // sessionStorage 不可用时跳过防重保护
        }
        if (alreadyConsumed) {
            return;
        }

        setIsConfirming(true);
        (async () => {
            try {
                const data = await confirmCommerceOrderWithPolling(
                    async args => {
                        const res = await CommerceApi.postOrderConfirmV1({
                            order_id: args.commerceOrderId,
                            client_result: {
                                status: ret.status,
                                store,
                                store_order_id: args.paypalOrderId,
                            },
                            ts: String(Date.now()),
                        });
                        return assertConfirmOrderResponse(res);
                    },
                    { commerceOrderId: ret.orderId, paypalOrderId: ret.storeOrderId }
                );

                const staticContext = meta
                    ? metaToStaticContext(meta)
                    : {
                          trigger_scene: '',
                          gear_id: '',
                          is_balance_enough: 0,
                          paywall_template_id: '',
                          version: 0,
                          checksum: '',
                          book_id: '',
                          chapter_id: '',
                          chapter_price: '',
                          is_auto_unlock_on: '',
                      };
                const payExtras = meta ? metaToPayExtras(meta) : { money: 0, goods_type: 'recharge' as const };

                if (data.status === 'PAID') {
                    reportPayPalSuccessFromMeta(ret.orderId, meta, returnScene);
                    queuePayResultTrackingAfterRefresh({ staticContext, payExtras });
                    useAssetStore.getState().markPaymentSuccess();
                } else {
                    reportPayPalFailFromMeta(
                        ret.orderId,
                        meta,
                        returnScene,
                        mapConfirmStatusToPayPalFailReason(data.status),
                        formatConfirmOrderFailDetail(data)
                    );
                    trackRechargePageResultPayFail({ staticContext, payExtras });
                    addToast('Payment not confirmed');
                }
            } catch (err) {
                reportPayPalFailFromMeta(
                    ret.orderId,
                    meta,
                    returnScene,
                    'order_confirm_api_failed',
                    readArmsFailDetail(err)
                );
                if (meta) {
                    trackRechargePageResultPayFail({
                        staticContext: metaToStaticContext(meta),
                        payExtras: metaToPayExtras(meta),
                    });
                }
                addToast('Order confirmation failed');
            } finally {
                clearPersistedPayPendingMeta(ret.orderId);
                clearPayPendingReturnUrls();
                setIsConfirming(false);
            }
        })().catch(() => {
            setIsConfirming(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 只处理一次回跳
    }, []);

    return (
        <>
            <LoadingAnimation
                mask
                textClassName='!text-[12px]'
                cardClassName='w-[165px]'
                visible={isConfirming}
                text={t('pay_paying')}
            />
            <PayPalCancelReasonModal visible={showCancelModal} onClose={handleCancelModalClose} orderId={orderId} />
        </>
    );
}
